import { selectTerritoryLicensingLimits } from '@/features/domain/territory'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { InputError, ModalData, useController } from '../../hooks/useController'
import { useControllerActions } from '../../hooks/useControllerActions'
import { useTexts } from '../../useTexts'

const MIN_RANGE_DAYS = 7

export const useRangePicker = () => {
  const { updateData, data, status } = useController()
  const { maxPlanningRange, planningRange } = useSelector(selectTerritoryLicensingLimits)
  const { onPlanningRangeChange } = useControllerActions()
  const [inputValue, setInputValue] = useState(planningRange.toString())
  const [isLoading, setIsLoading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const abortControllerRef = useRef<AbortController>()
  const debounceTimeoutRef = useRef<NodeJS.Timeout>() // For debouncing input changes
  const texts = useTexts()

  const updateRangeWithPercentage = useCallback(
    async (value: number, clearInputError = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()
      const { signal } = abortControllerRef.current

      setIsLoading(true)
      try {
        const result = await onPlanningRangeChange(value)
        if (signal.aborted) return

        // Check if percentage is 100% or more
        const percentage = result?.percentage ?? 0
        const hasStorageError = percentage >= 100

        const updatePayload: Partial<ModalData> = {
          newPlanningRange: value,
          percentage,
          ordersCanBeExported: (result?.ordersToBeExported ?? 0) > 0,
          isReduced: value < planningRange,
        }

        // Handle input error based on source and percentage
        if (clearInputError) {
          // Slider interaction - always clear input error
          updatePayload.inputError = { hasError: false, message: '' }
        } else {
          // Manual input - show input error if percentage >= 100%
          updatePayload.inputError = hasStorageError
            ? {
                hasError: true,
                message: texts.rangePickerAlertWithStorageError(value),
              }
            : { hasError: false, message: '' }
        }

        // Set storage error (for StorageAlert display)
        updatePayload.storageError = hasStorageError
          ? {
              hasError: true,
              message: texts.rangePickerAlertWithStorageError(value),
            }
          : { hasError: false, message: '' }

        updateData(updatePayload)
      } catch (error) {
        if (!signal.aborted) {
          console.error('Failed to update planning range:', error)
        }
      } finally {
        if (!signal.aborted) {
          setIsLoading(false)
        }
      }
    },
    [onPlanningRangeChange, updateData, planningRange],
  )

  const parseAndValidateInput = useCallback(
    (value: string): { value: number | null; error: InputError } => {
      const numericValue = Number(value)

      if (!Number.isInteger(numericValue)) {
        return { value: null, error: { hasError: false, message: '' } }
      }

      if (numericValue < MIN_RANGE_DAYS || numericValue > maxPlanningRange) {
        return {
          value: null,
          error: {
            hasError: true,
            message: texts.rangePickerAlertWithMinInputErrorOutOfRange(maxPlanningRange),
          },
        }
      }

      return { value: numericValue, error: { hasError: false, message: '' } }
    },
    [maxPlanningRange, texts],
  )

  useEffect(() => {
    updateRangeWithPercentage(planningRange, true)
  }, [])

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  const handleSliderChange = useCallback(
    async (_event: Event, newValue: number) => {
      setInputValue(newValue.toString())

      updateData({
        newPlanningRange: newValue,
      })
    },
    [updateData, planningRange],
  )

  const handleSliderChangeCommitted = useCallback(
    async (_event: Event | React.SyntheticEvent, newValue: number) => {
      setIsDragging(false)
      await updateRangeWithPercentage(newValue, true) // Clear input error when using slider
    },
    [updateRangeWithPercentage],
  )

  const handleSliderMouseDown = useCallback(() => {
    setIsDragging(true)
  }, [])

  const handleInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputVal = event.target.value
      setInputValue(inputVal)

      // Clear any existing debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      const { value: validatedValue, error } = parseAndValidateInput(inputVal)

      if (validatedValue !== null && !error.hasError) {
        // Valid input - debounce the API call
        debounceTimeoutRef.current = setTimeout(async () => {
          await updateRangeWithPercentage(validatedValue, false)
        }, 500) // Wait 500ms after user stops typing
      } else {
        // Invalid input - show input error immediately, clear storage error, don't call API
        updateData({
          newPlanningRange: data?.newPlanningRange ?? planningRange,
          inputError: error,
          storageError: { hasError: false, message: '' },
        })
      }
    },
    [
      parseAndValidateInput,
      updateRangeWithPercentage,
      updateData,
      data?.newPlanningRange,
      planningRange,
    ],
  )

  const generateDayLabels = useCallback(() => {
    const labels: number[] = []
    labels.push(MIN_RANGE_DAYS)

    const lastMultiple = Math.floor((maxPlanningRange - 1) / 7) * 7
    for (let i = MIN_RANGE_DAYS + 7; i < lastMultiple; i += 7) {
      labels.push(i)
    }

    if (maxPlanningRange > MIN_RANGE_DAYS) {
      labels.push(maxPlanningRange)
    }

    return labels
  }, [maxPlanningRange])

  const generateSliderMarks = useCallback(() => {
    return Array.from({ length: maxPlanningRange - MIN_RANGE_DAYS + 1 }, (_, i) => ({
      value: MIN_RANGE_DAYS + i,
      label: '',
    }))
  }, [maxPlanningRange])

  return {
    maxPlanningRange,
    min: MIN_RANGE_DAYS,
    inputValue,
    inputError: data?.inputError ?? { hasError: false, message: '' },
    storageError: data?.storageError ?? { hasError: false, message: '' },
    newPlanningRange: data?.newPlanningRange ?? planningRange,
    isLoading: isLoading || status === 'submitting',
    isDragging,
    handleSliderChange,
    handleSliderChangeCommitted,
    handleSliderMouseDown,
    handleInputChange,
    generateDayLabels,
    generateSliderMarks,
  }
}
